import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Provider } from "react-redux";
import { useEffect } from "react";
import { Toaster } from "sonner";
import store from "./store";
import { useAppSelector } from "./store/hooks";
import { useFetchUserQuery } from "./services/authApi";
import { AuthProvider } from "./context/AuthContext";
import { DemoDataProvider } from "./context/DemoDataContext";
import { Navbar } from "./pages/components/Navbar";
import { Login } from "./pages/Login";
import { Signup } from "./pages/Signup";
import { Dashboard } from "./pages/Dashboard";
import { Clients } from "./pages/Clients";
import { ClientDetail } from "./pages/ClientDetail";
import { Settings } from "./pages/settings/Index";
import "./App.css";
import {
  selectAuthUser,
  selectAuthToken,
  selectAuthLoading,
} from "./store/selectors/authSelectors";
import { selectThemeValue } from "./store/selectors/themeSelectors";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const user = useAppSelector(selectAuthUser);
  const token = useAppSelector(selectAuthToken);
  const loading = useAppSelector(selectAuthLoading);
  const { isFetching: isFetchingUser } = useFetchUserQuery(undefined, {
    skip: !token || !!user,
  });

  const isLoading = loading || isFetchingUser || (token && !user);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || !token) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const user = useAppSelector(selectAuthUser);
  const token = useAppSelector(selectAuthToken);
  const loading = useAppSelector(selectAuthLoading);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user && token) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Layout with Navbar
const Layout = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
  </>
);

function AppRoutes() {
  // Initialize theme on app load
  const theme = useAppSelector(selectThemeValue);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }, [theme]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <Navigate to="/login" replace />
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <Layout>
              <Clients />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ClientDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <DemoDataProvider>
          <BrowserRouter>
            <div className="min-h-screen bg-background">
              <AppRoutes />
              <Toaster
                position="top-right"
                richColors
                toastOptions={{
                  className: 'glass border border-border',
                }}
              />
            </div>
          </BrowserRouter>
        </DemoDataProvider>
      </AuthProvider>
    </Provider>
  );
}

export default App;
